import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

import teleConsultationApi from "../tele-consultation/teleConsultationApi";

import {
  TeleRTCClient,
  createRecordingStream,
  pickRecorderMimeType,
} from "../tele-consultation/webrtcservice";

const COLORS = {
  aquaBlue: "#11a8a4",
  grassGreen: "#7ed957",
  darkGrey: "#3c3b3f",
  white: "#ffffff",
  lightGrey: "#f3f4f6",
  mediumGrey: "#e5e7eb",
  darkText: "#1f2937",
  mediumText: "#6b7280",
  lightText: "#9ca3af",
  red: "#dc2626",
  yellow: "#d97706",
};

const TERMINAL_STATUSES = ["COMPLETED", "CANCELLED"];
const ACTIVE_STATUSES = ["WAITING", "ONGOING"];

const POLL_WAITING_MS = 4000;
const POLL_JOINED_MS = 8000;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const getStorageJSON = (key) => {
  try {
    const value = sessionStorage.getItem(key);

    if (!value) {
      return null;
    }

    return JSON.parse(value);
  } catch (error) {
    console.error(`Unable to read sessionStorage key: ${key}`, error);
    return null;
  }
};

const setStorageJSON = (key, value) => {
  try {
    if (value) {
      sessionStorage.setItem(key, JSON.stringify(value));
    }
  } catch (error) {
    console.error(`Unable to write sessionStorage key: ${key}`, error);
  }
};

const getErrorMessage = (error) => {
  if (!error) {
    return "Something went wrong.";
  }

  if (error.response) {
    const status = error.response.status;
    const data = error.response.data;

    if (status === 401) {
      return "Your session has expired. Please login again.";
    }

    if (status === 403) {
      return "You are not authorized to access this consultation.";
    }

    if (status === 404) {
      return "Consultation not found.";
    }

    if (status >= 500) {
      return "The server encountered an error. Please try again in a moment.";
    }

    if (data) {
      if (typeof data === "string" && data.length < 300) {
        return data;
      }

      if (typeof data === "object") {
        if (data.detail) return String(data.detail);
        if (data.message) return String(data.message);
        if (data.error) return String(data.error);

        const firstKey = Object.keys(data)[0];

        if (firstKey) {
          const value = data[firstKey];

          if (Array.isArray(value) && value[0]) {
            return String(value[0]);
          }

          if (typeof value === "string") {
            return value;
          }
        }
      }
    }

    return "Unable to process the request.";
  }

  if (error.request) {
    return "Unable to connect to the server. Please check your internet connection.";
  }

  if (error.message) {
    return error.message;
  }

  return "Unable to process the request.";
};

const describeMediaError = (error, kind = "both") => {
  const name = error?.name;

  if (name === "NotAllowedError" || name === "PermissionDeniedError" || name === "SecurityError") {
    if (kind === "camera") {
      return "Camera access was denied. Please allow camera access in your browser settings.";
    }

    if (kind === "microphone") {
      return "Microphone access was denied. Please allow microphone access in your browser settings.";
    }

    return "Camera and microphone access was denied. Please allow access in your browser settings and try again.";
  }

  if (name === "NotFoundError" || name === "DevicesNotFoundError") {
    return "No camera or microphone was found on this device.";
  }

  if (name === "NotReadableError" || name === "TrackStartError") {
    return "Your camera or microphone is being used by another application. Close it and try again.";
  }

  return "Unable to access the camera and microphone. Please check your device and try again.";
};

const formatDateTime = (value) => {
  if (!value) {
    return "N/A";
  }

  try {
    return new Date(value).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
};

const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) {
    return "N/A";
  }

  const totalSeconds = Math.floor(Number(seconds));

  if (Number.isNaN(totalSeconds)) {
    return "N/A";
  }

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  }

  return `${minutes}m ${remainingSeconds}s`;
};

const formatClock = (seconds) => {
  const total = Math.max(0, Number(seconds) || 0);
  const minutes = String(Math.floor(total / 60)).padStart(2, "0");
  const secs = String(total % 60).padStart(2, "0");

  return `${minutes}:${secs}`;
};

const getMemberName = (member) => {
  if (!member) {
    return "N/A";
  }

  return (
    member.name ||
    member.full_name ||
    member.member_name ||
    `${member.first_name || ""} ${member.last_name || ""}`.trim() ||
    "N/A"
  );
};

const getPackageName = (pkg) => {
  if (!pkg) {
    return "N/A";
  }

  return (
    pkg.name ||
    pkg.package_name ||
    pkg.title ||
    `Package #${pkg.id || pkg.package_id || "N/A"}`
  );
};

const getServiceName = (service) => {
  if (!service) {
    return "N/A";
  }

  return (
    service.name ||
    service.service_name ||
    service.title ||
    `Service #${service.id || service.service_id || "N/A"}`
  );
};

const getFamilyHeadName = (family) => {
  if (!family) {
    return "N/A";
  }

  return (
    family.head_name ||
    family.head?.name ||
    family.family_head?.name ||
    "N/A"
  );
};

/* ------------------------------------------------------------------ */
/* Small presentational components                                     */
/* ------------------------------------------------------------------ */

const InfoItem = ({ label, value }) => {
  return (
    <div style={styles.infoItem}>
      <span style={styles.infoLabel}>{label}</span>
      <span style={styles.infoValue}>{value}</span>
    </div>
  );
};

const PatientInfoCard = ({ family, member, pkg, service }) => {
  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div>
          <h2 style={styles.cardTitle}>Patient Information</h2>
          <p style={styles.cardSubtitle}>
            Family member selected for teleconsultation
          </p>
        </div>

        <div style={styles.patientAvatar}>
          {getMemberName(member).charAt(0).toUpperCase()}
        </div>
      </div>

      <div className="tc-info-grid">
        <InfoItem label="Patient Name" value={getMemberName(member)} />
        <InfoItem label="Member ID" value={member?.member_id || "N/A"} />
        <InfoItem
          label="Age"
          value={member?.age ?? member?.calculated_age ?? "N/A"}
        />
        <InfoItem label="Gender" value={member?.gender || "N/A"} />
        <InfoItem
          label="Mobile"
          value={member?.mobile || member?.phone || member?.mobile_number || "N/A"}
        />
        <InfoItem label="Family ID" value={family?.family_id || "N/A"} />
        <InfoItem label="Family Head" value={getFamilyHeadName(family)} />
        <InfoItem label="Package" value={getPackageName(pkg)} />
        <InfoItem label="Service" value={getServiceName(service)} />
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  let background = "#fef3c7";
  let color = COLORS.yellow;

  if (status === "ONGOING") {
    background = "#dcfce7";
    color = "#15803d";
  }

  if (status === "COMPLETED") {
    background = "#e0f2fe";
    color = "#0369a1";
  }

  if (status === "CANCELLED") {
    background = "#fee2e2";
    color = COLORS.red;
  }

  return (
    <span
      style={{
        ...styles.statusBadge,
        backgroundColor: background,
        color,
      }}
    >
      {status || "UNKNOWN"}
    </span>
  );
};

const WaitingRoom = ({ consultation, doctorStarted, busy, onJoin }) => {
  const isWaiting = consultation?.status === "WAITING";

  return (
    <div style={styles.waitingCard}>
      <div
        style={{
          ...styles.waitingIcon,
          ...(doctorStarted ? styles.waitingIconSuccess : {}),
        }}
      >
        {doctorStarted ? "✓" : "◷"}
      </div>

      <h2 style={styles.waitingTitle}>
        {doctorStarted
          ? "Doctor has started the meeting"
          : "Waiting for Doctor"}
      </h2>

      <p style={styles.waitingDescription}>
        {doctorStarted
          ? "The doctor has started the consultation. You can now join the meeting."
          : "The consultation request has been created. Please wait while the doctor starts the meeting."}
      </p>

      <div className="tc-meta-grid">
        <div style={styles.metaItem}>
          <span style={styles.metaLabel}>Consultation ID</span>
          <span style={styles.metaValue}>
            {consultation?.consultation_id || "N/A"}
          </span>
        </div>

        <div style={styles.metaItem}>
          <span style={styles.metaLabel}>Room ID</span>
          <span style={styles.metaValue}>{consultation?.room_id || "N/A"}</span>
        </div>

        <div style={styles.metaItem}>
          <span style={styles.metaLabel}>Status</span>
          <span style={styles.metaValue}>
            <StatusBadge status={consultation?.status} />
          </span>
        </div>
      </div>

      {isWaiting && (
        <div style={styles.waitingAnimation} role="status" aria-live="polite">
          <span className="tc-dot" style={styles.dot}></span>
          <span>Waiting for doctor...</span>
        </div>
      )}

      {doctorStarted && (
        <button
          type="button"
          onClick={onJoin}
          disabled={busy}
          title="Join meeting"
          aria-label="Join meeting"
          style={{
            ...styles.primaryButton,
            opacity: busy ? 0.6 : 1,
            cursor: busy ? "not-allowed" : "pointer",
          }}
        >
          {busy ? "Joining Meeting..." : "Join Meeting"}
        </button>
      )}
    </div>
  );
};

const PreJoin = ({
  localVideoRef,
  requestingMedia,
  isMediaReady,
  mediaError,
  mediaWarning,
  hasAudio,
  hasVideo,
  joining,
  onRetry,
  onConfirm,
  onCancel,
  member,
}) => {
  const ready = isMediaReady && !mediaError;

  return (
    <div style={styles.prejoinCard}>
      <h2 style={styles.prejoinTitle}>
        {requestingMedia ? "Allow Camera & Microphone" : "Ready to join?"}
      </h2>

      <p style={styles.prejoinText}>
        {requestingMedia
          ? "Your browser is asking for permission. Choose Allow to continue."
          : "Check your camera and microphone, then join the meeting."}
      </p>

      <div style={styles.prejoinPreview}>
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          style={{
            ...styles.mirroredVideo,
            display: ready && hasVideo ? "block" : "none",
          }}
        />

        {!(ready && hasVideo) && (
          <div style={styles.prejoinPlaceholder}>
            {requestingMedia ? (
              <>
                <div className="tc-spinner" style={styles.spinner}></div>
                <span>Waiting for permission...</span>
              </>
            ) : (
              <>
                <div style={styles.largeAvatar}>
                  {getMemberName(member).charAt(0).toUpperCase()}
                </div>
                <span>{ready ? "Camera unavailable" : "Camera preview"}</span>
              </>
            )}
          </div>
        )}
      </div>

      <div style={styles.deviceRow}>
        <span
          style={{
            ...styles.deviceChip,
            ...(ready && hasAudio ? styles.deviceChipOk : styles.deviceChipOff),
          }}
        >
          🎤 Microphone: {ready && hasAudio ? "Ready" : "Not available"}
        </span>

        <span
          style={{
            ...styles.deviceChip,
            ...(ready && hasVideo ? styles.deviceChipOk : styles.deviceChipOff),
          }}
        >
          📹 Camera: {ready && hasVideo ? "Ready" : "Not available"}
        </span>
      </div>

      {mediaWarning && <div style={styles.inlineWarning}>{mediaWarning}</div>}

      {mediaError && <div style={styles.inlineError}>{mediaError}</div>}

      <div style={styles.buttonRow}>
        <button
          type="button"
          onClick={onCancel}
          disabled={joining}
          title="Cancel"
          aria-label="Cancel"
          style={styles.secondaryButton}
        >
          Cancel
        </button>

        {mediaError ? (
          <button
            type="button"
            onClick={onRetry}
            disabled={requestingMedia}
            title="Try again"
            aria-label="Try again"
            style={{
              ...styles.primaryButton,
              opacity: requestingMedia ? 0.6 : 1,
            }}
          >
            Try Again
          </button>
        ) : (
          <button
            type="button"
            onClick={onConfirm}
            disabled={!ready || joining}
            title="Join meeting"
            aria-label="Join meeting"
            style={{
              ...styles.primaryButton,
              opacity: !ready || joining ? 0.6 : 1,
              cursor: !ready || joining ? "not-allowed" : "pointer",
            }}
          >
            {joining ? "Joining Meeting..." : "Join Meeting"}
          </button>
        )}
      </div>
    </div>
  );
};

const CallScreen = ({
  consultation,
  member,
  localVideoRef,
  remoteVideoRef,
  micEnabled,
  cameraEnabled,
  hasAudio,
  hasVideo,
  hasRemoteStream,
  isWebSocketConnected,
  isWebRTCConnected,
  reconnecting,
  connectionError,
  isRecording,
  recordingDuration,
  uploadingRecording,
  recordingMessage,
  leaving,
  onToggleMic,
  onToggleCamera,
  onToggleRecording,
  onLeave,
}) => {
  let statusText = "Connecting to Doctor...";
  let statusColor = COLORS.yellow;

  if (reconnecting) {
    statusText = "Connection interrupted. Reconnecting...";
    statusColor = COLORS.red;
  } else if (isWebSocketConnected && isWebRTCConnected) {
    statusText = "Connected";
    statusColor = COLORS.grassGreen;
  }

  const cameraShown = cameraEnabled && hasVideo;

  return (
    <div style={styles.videoContainer}>
      <div style={styles.videoHeader}>
        <div style={styles.videoHeaderLeft}>
          <strong style={styles.videoHeaderTitle}>Teleconsultation</strong>

          <span style={{ ...styles.connectedStatus, color: statusColor }}>
            ● {statusText}
          </span>

          {isRecording && (
            <span className="tc-rec" style={styles.recBadge}>
              ● REC {formatClock(recordingDuration)}
            </span>
          )}
        </div>

        <div style={styles.videoRoom}>Room: {consultation?.room_id || "N/A"}</div>
      </div>

      <div className="tc-video-area" style={styles.videoArea}>
        <video
          ref={remoteVideoRef}
          autoPlay
          playsInline
          style={{
            ...styles.remoteVideo,
            visibility: hasRemoteStream ? "visible" : "hidden",
          }}
        />

        {!hasRemoteStream && (
          <div style={styles.doctorPlaceholder}>
            <div style={styles.doctorAvatar}>D</div>
            <h3 style={styles.doctorPlaceholderTitle}>Doctor</h3>
            <p style={styles.doctorPlaceholderText}>
              {isWebSocketConnected
                ? "Waiting for Doctor video..."
                : "Connecting to Doctor..."}
            </p>
          </div>
        )}

        {hasRemoteStream && <div style={styles.remoteLabel}>Doctor</div>}

        <div className="tc-local" style={styles.localPreview}>
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{
              ...styles.mirroredVideo,
              visibility: cameraShown ? "visible" : "hidden",
            }}
          />

          {!cameraShown && (
            <div style={styles.cameraOff}>
              <div style={styles.smallAvatar}>
                {getMemberName(member).charAt(0).toUpperCase()}
              </div>
              <span>{hasVideo ? "Camera Off" : "No Camera"}</span>
            </div>
          )}

          <div style={styles.localLabel}>You (Technician)</div>
        </div>
      </div>

      {(connectionError || recordingMessage || uploadingRecording) && (
        <div style={styles.callNotice}>
          {connectionError ||
            (uploadingRecording ? "Uploading recording..." : recordingMessage)}
        </div>
      )}

      <div className="tc-controls" style={styles.callControls}>
        <button
          type="button"
          onClick={onToggleMic}
          disabled={!hasAudio}
          style={{
            ...styles.controlButton,
            ...(micEnabled && hasAudio ? {} : styles.controlButtonDanger),
            opacity: hasAudio ? 1 : 0.5,
          }}
          title={micEnabled ? "Mute microphone" : "Unmute microphone"}
          aria-label={micEnabled ? "Mute microphone" : "Unmute microphone"}
          aria-pressed={!micEnabled}
        >
          {micEnabled && hasAudio ? "🎤" : "🔇"}
        </button>

        <button
          type="button"
          onClick={onToggleCamera}
          disabled={!hasVideo}
          style={{
            ...styles.controlButton,
            ...(cameraEnabled && hasVideo ? {} : styles.controlButtonDanger),
            opacity: hasVideo ? 1 : 0.5,
          }}
          title={cameraEnabled ? "Turn camera off" : "Turn camera on"}
          aria-label={cameraEnabled ? "Turn camera off" : "Turn camera on"}
          aria-pressed={!cameraEnabled}
        >
          {cameraEnabled && hasVideo ? "📹" : "🚫"}
        </button>

        <button
          type="button"
          onClick={onToggleRecording}
          disabled={uploadingRecording || leaving}
          style={{
            ...styles.controlButton,
            ...(isRecording ? styles.controlButtonRecording : {}),
            opacity: uploadingRecording || leaving ? 0.5 : 1,
          }}
          title={isRecording ? "Stop recording" : "Start recording"}
          aria-label={isRecording ? "Stop recording" : "Start recording"}
          aria-pressed={isRecording}
        >
          {isRecording ? "⏹" : "⏺"}
        </button>

        <button
          type="button"
          onClick={onLeave}
          disabled={leaving}
          style={{
            ...styles.leaveButton,
            opacity: leaving ? 0.6 : 1,
          }}
          title="Leave meeting"
          aria-label="Leave meeting"
        >
          ☎ {leaving ? "Leaving..." : "Leave"}
        </button>
      </div>
    </div>
  );
};

const ConsultationSummary = ({ consultation, onBack, onNew, creating }) => {
  const cancelled = consultation?.status === "CANCELLED";

  return (
    <div style={styles.summaryCard}>
      <div
        style={{
          ...styles.completedIcon,
          ...(cancelled ? styles.cancelledIcon : {}),
        }}
      >
        {cancelled ? "×" : "✓"}
      </div>

      <h2 style={styles.summaryTitle}>
        {cancelled ? "Consultation Cancelled" : "Consultation Completed"}
      </h2>

      <p style={styles.summaryDescription}>
        {cancelled
          ? "This teleconsultation was cancelled."
          : "This teleconsultation has been completed successfully."}
      </p>

      <div className="tc-summary-grid">
        <InfoItem
          label="Consultation ID"
          value={consultation?.consultation_id || "N/A"}
        />
        <InfoItem label="Status" value={consultation?.status || "N/A"} />
        <InfoItem
          label="Started At"
          value={formatDateTime(consultation?.started_at)}
        />
        <InfoItem
          label="Ended At"
          value={formatDateTime(consultation?.ended_at)}
        />
        <InfoItem
          label="Duration"
          value={formatDuration(consultation?.duration_seconds)}
        />
      </div>

      <div style={styles.buttonRow}>
        {onNew && (
          <button
            type="button"
            onClick={onNew}
            disabled={creating}
            title="Request a new consultation"
            aria-label="Request a new consultation"
            style={{ ...styles.secondaryButton, opacity: creating ? 0.6 : 1 }}
          >
            New Consultation
          </button>
        )}

        <button
          type="button"
          onClick={onBack}
          title="Back to dashboard"
          aria-label="Back to dashboard"
          style={styles.primaryButton}
        >
          Back to Dashboard
        </button>
      </div>
    </div>
  );
};

const Modal = ({ title, children, actions }) => {
  return (
    <div style={styles.modalOverlay} role="dialog" aria-modal="true" aria-label={title}>
      <div style={styles.modal}>
        <h3 style={styles.modalTitle}>{title}</h3>
        <div style={styles.modalBody}>{children}</div>
        <div style={styles.modalActions}>{actions}</div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main page                                                           */
/* ------------------------------------------------------------------ */

const TechnicianTeleServices = () => {
  const { familyId, memberId } = useParams();

  const location = useLocation();
  const navigate = useNavigate();

  const locationState = location.state || {};

  const [assignment] = useState(
    () =>
      locationState.assignment ||
      getStorageJSON("mfi_current_assignment")
  );

  const [family] = useState(
    () =>
      locationState.family ||
      getStorageJSON("mfi_current_family") ||
      assignment?.family ||
      null
  );

  const [member] = useState(
    () =>
      locationState.member ||
      getStorageJSON("mfi_current_member")
  );

  const [pkg] = useState(
    () =>
      locationState.package ||
      getStorageJSON("mfi_current_package") ||
      assignment?.package ||
      family?.package ||
      null
  );

  const [service] = useState(() => {
    // 1. Explicit service passed during navigation
    if (locationState.service) {
        return locationState.service;
    }

    // 2. Previously stored service
    const storedService = getStorageJSON("mfi_current_service");

    // Only reuse it if it is actually Tele Consultation
    if (
        storedService &&
        (
        storedService.type === "CONSULTATION" ||
        storedService.name === "Tele Consultation" ||
        Number(storedService.id ?? storedService.service_id) === 33
        )
    ) {
        return storedService;
    }

    // 3. Find Tele Consultation from assignment.services
    const assignedServices = assignment?.services || [];

    const teleService = assignedServices.find(
        (svc) =>
        svc?.type === "CONSULTATION" ||
        svc?.name === "Tele Consultation" ||
        Number(svc?.id ?? svc?.service_id) === 33
    );

    return teleService || null;
    });

  const [consultation, setConsultation] = useState(null);

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const [error, setError] = useState("");
  const [pollError, setPollError] = useState("");

  const [prejoin, setPrejoin] = useState(false);
  const [joined, setJoined] = useState(false);

  const [micEnabled, setMicEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(true);

  const [requestingMedia, setRequestingMedia] = useState(false);
  const [isMediaReady, setIsMediaReady] = useState(false);
  const [mediaError, setMediaError] = useState("");
  const [mediaWarning, setMediaWarning] = useState("");
  const [hasAudio, setHasAudio] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);

  const [isWebSocketConnected, setIsWebSocketConnected] = useState(false);
  const [isWebRTCConnected, setIsWebRTCConnected] = useState(false);
  const [hasRemoteStream, setHasRemoteStream] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState("");

  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [recordingMessage, setRecordingMessage] = useState("");
  const [showRecordNotice, setShowRecordNotice] = useState(false);

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Refs (heavy browser objects never live in React state)
  const createStartedRef = useRef(false);
  const pollingRef = useRef(null);
  const mountedRef = useRef(true);

  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const rtcClientRef = useRef(null); // owns RTCPeerConnection + WebSocket + reconnect timer
  const mediaRequestRef = useRef(null);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const recordingCleanupRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const recordingStartRef = useRef(0);

  const joinedRef = useRef(false);
  const joiningRef = useRef(false);
  const leavingRef = useRef(false);
  const isRecordingRef = useRef(false);
  const consultationIdRef = useRef(null);
  const consultationStatusRef = useRef(null);

  const consultationStorageKey = `tele_consultation_${familyId}_${memberId}`;

  const doctorStarted = consultation?.status === "ONGOING";

  // Persist navigation state so a refresh keeps working.
  useEffect(() => {
    setStorageJSON("mfi_current_family", family);
    setStorageJSON("mfi_current_member", member);
    setStorageJSON("mfi_current_package", pkg);
    setStorageJSON("mfi_current_service", service);
    setStorageJSON("mfi_current_assignment", assignment);
  }, [family, member, pkg, service, assignment]);

  /* ---------------- Polling / REST ---------------- */

  const cleanupPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const saveConsultation = useCallback(
    (response) => {
        if (!response) {
        return;
        }

        // API may return either:
        //
        // {
        //   consultation_id: "...",
        //   status: "WAITING"
        // }
        //
        // OR:
        //
        // {
        //   message: "...",
        //   consultation: {
        //      consultation_id: "...",
        //      status: "WAITING"
        //   }
        // }

        const data = response.consultation || response;

        if (!data?.consultation_id) {
        console.error(
            "[TeleConsultation] Invalid consultation response:",
            response
        );
        return;
        }

        consultationIdRef.current = data.consultation_id;
        consultationStatusRef.current = data.status || null;

        setConsultation((previous) =>
        JSON.stringify(previous) === JSON.stringify(data)
            ? previous
            : data
        );

        try {
        sessionStorage.setItem(
            consultationStorageKey,
            JSON.stringify(data)
        );
        } catch (storageError) {
        console.error(
            "Unable to save consultation:",
            storageError
        );
        }
    },
    [consultationStorageKey]
    );

  const fetchConsultation = useCallback(
    async (consultationId) => {
      const data = await teleConsultationApi.getConsultation(consultationId);
      saveConsultation(data);
      return data;
    },
    [saveConsultation]
  );

  const startPolling = useCallback(
    (consultationId, intervalMs = POLL_WAITING_MS, immediate = false) => {
      cleanupPolling();

      if (!consultationId || !mountedRef.current) {
        return;
      }

      const poll = async () => {
        try {
          console.log("[TeleConsultation] Polling consultation");

          const data = await fetchConsultation(consultationId);

          if (!mountedRef.current) {
            return;
          }

          setPollError("");

          if (data.status === "ONGOING" && !joinedRef.current) {
            console.log("[TeleConsultation] Doctor started");
          }

          if (TERMINAL_STATUSES.includes(data.status)) {
            cleanupPolling();
          }
        } catch (requestError) {
          console.error("Unable to load consultation:", requestError);

          if (!mountedRef.current) {
            return;
          }

          setPollError(getErrorMessage(requestError));

          if (requestError?.response?.status === 401) {
            cleanupPolling();
          }
        }
      };

      if (immediate) {
        poll();
      }

      pollingRef.current = setInterval(poll, intervalMs);
    },
    [cleanupPolling, fetchConsultation]
  );

  const createConsultation = useCallback(
    async (force = false) => {
      if (createStartedRef.current) {
        return;
      }

      createStartedRef.current = true;

      setCreating(true);
      setError("");

      try {
        if (!force) {
          const stored = getStorageJSON(consultationStorageKey);

          if (stored?.consultation_id) {
            try {
              // Backend is the source of truth, never trust stale storage.
              const latest = await fetchConsultation(stored.consultation_id);

              if (ACTIVE_STATUSES.includes(latest.status)) {
                console.log("[TeleConsultation] Reusing consultation");
                startPolling(latest.consultation_id, POLL_WAITING_MS);
                return;
              }

              if (TERMINAL_STATUSES.includes(latest.status)) {
                return; // summary is shown
              }

              sessionStorage.removeItem(consultationStorageKey);
            } catch (lookupError) {
              const status = lookupError?.response?.status;

              if (status === 400 || status === 403 || status === 404) {
                sessionStorage.removeItem(consultationStorageKey);
              } else {
                throw lookupError; // network/auth/server: do not create a duplicate
              }
            }
          }
        }

        if (!familyId) {
          throw new Error("Family ID is missing.");
        }

        if (!memberId) {
          throw new Error("Member ID is missing.");
        }

        const packageId = pkg?.id ?? pkg?.package_id;
        const serviceId = service?.id ?? service?.service_id;

        if (!packageId) {
          throw new Error("Package ID is missing.");
        }

        if (!serviceId) {
          throw new Error("Service ID is missing.");
        }

        const payload = {
          family_id: familyId,
          member_id: memberId,
          package_id: packageId,
          service_id: serviceId,
        };

        console.log("[TeleConsultation] Creating consultation");

        const response = await teleConsultationApi.createConsultation(payload);

        console.log(
        "[TeleConsultation] Consultation API response:",
        response
        );

        const data = response?.consultation || response;

        if (!data?.consultation_id) {
        throw new Error(
            "Consultation was created but consultation details were not returned."
        );
        }

        console.log(
        "[TeleConsultation] Consultation:",
        data
        );

        saveConsultation(data);

        if (ACTIVE_STATUSES.includes(data.status)) {
        startPolling(
            data.consultation_id,
            POLL_WAITING_MS,
            true
        );
        }
      } catch (requestError) {
        console.error("Create consultation error:", requestError);

        createStartedRef.current = false;

        if (mountedRef.current) {
          setError(getErrorMessage(requestError));
        }
      } finally {
        if (mountedRef.current) {
          setCreating(false);
          setLoading(false);
        }
      }
    },
    [
      familyId,
      memberId,
      pkg,
      service,
      consultationStorageKey,
      fetchConsultation,
      saveConsultation,
      startPolling,
    ]
  );

  /* ---------------- Local media ---------------- */

  const stopLocalMedia = useCallback(() => {
    const stream = localStreamRef.current;

    if (stream) {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (stopError) {
          console.error("Unable to stop track", stopError);
        }
      });
    }

    localStreamRef.current = null;
    mediaRequestRef.current = null;

    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }

    setIsMediaReady(false);
    setHasAudio(false);
    setHasVideo(false);
  }, []);

  const acquireMedia = useCallback(async () => {
    if (localStreamRef.current) {
      return localStreamRef.current;
    }

    if (mediaRequestRef.current) {
      return mediaRequestRef.current;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setMediaError(
        "Camera and microphone access is not supported by this browser."
      );
      return null;
    }

    setRequestingMedia(true);
    setMediaError("");
    setMediaWarning("");

    const request = (async () => {
      const getMedia = (constraints) =>
        navigator.mediaDevices.getUserMedia(constraints);

      let stream = null;
      let warning = "";

      try {
        stream = await getMedia({ video: true, audio: true });
      } catch (bothError) {
        console.error("getUserMedia (audio+video) failed:", bothError);

        try {
          stream = await getMedia({ video: true });
          warning = `${describeMediaError(bothError, "microphone")} You can continue with camera only.`;
        } catch (videoOnlyError) {
          console.error("getUserMedia (video) failed:", videoOnlyError);

          try {
            stream = await getMedia({ audio: true });
            warning = `${describeMediaError(videoOnlyError, "camera")} You can continue with microphone only.`;
          } catch (audioOnlyError) {
            console.error("getUserMedia (audio) failed:", audioOnlyError);

            if (mountedRef.current) {
              setMediaError(describeMediaError(bothError, "both"));
            }

            return null;
          }
        }
      }

      if (!mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return null;
      }

      localStreamRef.current = stream;

      const audioTracks = stream.getAudioTracks();
      const videoTracks = stream.getVideoTracks();

      setHasAudio(audioTracks.length > 0);
      setHasVideo(videoTracks.length > 0);
      setMicEnabled(audioTracks.length > 0);
      setCameraEnabled(videoTracks.length > 0);
      setMediaWarning(warning);
      setIsMediaReady(true);

      return stream;
    })();

    mediaRequestRef.current = request;

    try {
      return await request;
    } finally {
      mediaRequestRef.current = null;

      if (mountedRef.current) {
        setRequestingMedia(false);
      }
    }
  }, []);

  // Attach the local stream to whichever <video> is currently mounted.
  useEffect(() => {
    if (localVideoRef.current && localStreamRef.current) {
      if (localVideoRef.current.srcObject !== localStreamRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
      }
    }
  }, [prejoin, joined, isMediaReady, cameraEnabled]);

  // Attach the remote stream.
  useEffect(() => {
    if (remoteVideoRef.current) {
      const stream = remoteStreamRef.current;

      if (remoteVideoRef.current.srcObject !== stream) {
        remoteVideoRef.current.srcObject = stream || null;
      }
    }
  }, [joined, hasRemoteStream]);

  /* ---------------- Recording ---------------- */

  const clearRecordingTimer = useCallback(() => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, []);

  const stopRecording = useCallback(
    (upload = true) =>
      new Promise((resolve) => {
        const recorder = mediaRecorderRef.current;

        if (!recorder || recorder.state === "inactive") {
          resolve();
          return;
        }

        recorder.onstop = async () => {
          clearRecordingTimer();

          recordingCleanupRef.current?.();
          recordingCleanupRef.current = null;

          const durationSeconds = Math.round(
            (Date.now() - recordingStartRef.current) / 1000
          );

          const mimeType = recorder.mimeType || "video/webm";
          const blob = new Blob(recordingChunksRef.current, { type: mimeType });

          recordingChunksRef.current = [];
          mediaRecorderRef.current = null;
          isRecordingRef.current = false;

          if (mountedRef.current) {
            setIsRecording(false);
          }

          const id = consultationIdRef.current;

          if (upload && blob.size > 0 && id) {
            try {
              if (mountedRef.current) {
                setUploadingRecording(true);
                setRecordingMessage("");
              }

              const extension = mimeType.includes("mp4") ? "mp4" : "webm";
              const formData = new FormData();

              formData.append("file", blob, `consultation_${id}.${extension}`);
              formData.append("duration_seconds", String(durationSeconds));

              await teleConsultationApi.uploadRecording(id, formData);

              if (mountedRef.current) {
                setRecordingMessage("Recording uploaded successfully.");
              }
            } catch (uploadError) {
              console.error("Recording upload failed:", uploadError);

              if (mountedRef.current) {
                setRecordingMessage(
                  `Recording could not be uploaded. ${getErrorMessage(uploadError)}`
                );
              }
            } finally {
              if (mountedRef.current) {
                setUploadingRecording(false);
              }
            }
          }

          resolve();
        };

        try {
          recorder.stop();
        } catch (stopError) {
          console.error("Unable to stop recorder", stopError);
          resolve();
        }
      }),
    [clearRecordingTimer]
  );

  const confirmStartRecording = useCallback(() => {
    setShowRecordNotice(false);

    if (isRecordingRef.current || mediaRecorderRef.current) {
      return;
    }

    if (typeof MediaRecorder === "undefined") {
      setRecordingMessage("Recording is not supported by this browser.");
      return;
    }

    const localStream = localStreamRef.current;

    if (!localStream) {
      setRecordingMessage("Recording is unavailable because media is not ready.");
      return;
    }

    try {
      const { stream, cleanup } = createRecordingStream(
        localStream,
        remoteStreamRef.current
      );

      if (stream.getTracks().length === 0) {
        cleanup();
        setRecordingMessage("There is no audio or video available to record.");
        return;
      }

      const mimeType = pickRecorderMimeType();

      const recorder = new MediaRecorder(
        stream,
        mimeType ? { mimeType } : undefined
      );

      recordingChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          recordingChunksRef.current.push(event.data);
        }
      };

      recorder.onerror = (event) => {
        console.error("MediaRecorder error", event);
        setRecordingMessage("Recording failed. Please try again.");
      };

      recorder.start(1000);

      mediaRecorderRef.current = recorder;
      recordingCleanupRef.current = cleanup;
      recordingStartRef.current = Date.now();
      isRecordingRef.current = true;

      setRecordingMessage("");
      setRecordingDuration(0);
      setIsRecording(true);

      clearRecordingTimer();

      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(
          Math.round((Date.now() - recordingStartRef.current) / 1000)
        );
      }, 1000);
    } catch (recordError) {
      console.error("Unable to start recording", recordError);
      setRecordingMessage("Recording could not be started in this browser.");
    }
  }, [clearRecordingTimer]);

  const handleToggleRecording = () => {
    if (uploadingRecording) {
      return;
    }

    if (isRecordingRef.current) {
      stopRecording(true);
    } else {
      setShowRecordNotice(true);
    }
  };

  /* ---------------- WebRTC session ---------------- */

  const teardownCall = useCallback(() => {
    joinedRef.current = false;

    if (rtcClientRef.current) {
      rtcClientRef.current.close(true);
      rtcClientRef.current = null;
    }

    stopLocalMedia();

    remoteStreamRef.current = null;

    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }

    setIsWebSocketConnected(false);
    setIsWebRTCConnected(false);
    setHasRemoteStream(false);
    setReconnecting(false);
    setConnectionError("");
  }, [stopLocalMedia]);

  const startRtc = useCallback((consultationId) => {
    if (rtcClientRef.current || !localStreamRef.current) {
      return;
    }

    const client = new TeleRTCClient({
      consultationId,
      participantType: "TECHNICIAN",
      polite: true,
      localStream: localStreamRef.current,
      shouldReconnect: () =>
        joinedRef.current &&
        mountedRef.current &&
        !TERMINAL_STATUSES.includes(consultationStatusRef.current),
      callbacks: {
        onWsState: (connected) => {
          if (mountedRef.current) {
            setIsWebSocketConnected(connected);
          }
        },
        onRtcState: (state) => {
          if (!mountedRef.current) {
            return;
          }

          setIsWebRTCConnected(state === "connected");

          if (state === "connected") {
            setConnectionError("");
          }

          if (state === "failed") {
            setConnectionError("The media connection failed. Trying to recover...");
          }
        },
        onReconnecting: (value) => {
          if (mountedRef.current) {
            setReconnecting(value);
          }
        },
        onRemoteStream: (stream) => {
            console.log(
                "[Technician] Remote stream received:",
                stream
            );

            if (stream) {
                console.log("[Technician] Remote audio tracks:",
                stream.getAudioTracks().length
                );

                console.log("[Technician] Remote video tracks:",
                stream.getVideoTracks().length
                );
            }

            remoteStreamRef.current = stream || null;

            if (remoteVideoRef.current) {
                remoteVideoRef.current.srcObject = stream || null;

                if (stream) {
                remoteVideoRef.current
                    .play()
                    .catch((error) => {
                    console.warn(
                        "[Technician] Remote video play failed:",
                        error
                    );
                    });
                }
            }

            if (mountedRef.current) {
                setHasRemoteStream(Boolean(stream));
            }
            },
        onRemoteLeft: () => {
          if (mountedRef.current) {
            setIsWebRTCConnected(false);
          }
        },
        onError: (message) => {
          if (mountedRef.current) {
            setConnectionError(message);
          }
        },
      },
    });

    rtcClientRef.current = client;
    client.start();
  }, []);

  /* ---------------- User actions ---------------- */

  const handleOpenPrejoin = async () => {
    if (!doctorStarted || requestingMedia || prejoin || joinedRef.current) {
      return;
    }

    setError("");
    setPrejoin(true);

    await acquireMedia();
  };

  const handleRetryMedia = async () => {
    if (requestingMedia) {
      return;
    }

    stopLocalMedia();
    await acquireMedia();
  };

  const handleCancelPrejoin = () => {
    if (joiningRef.current) {
      return;
    }

    stopLocalMedia();
    setMediaError("");
    setMediaWarning("");
    setPrejoin(false);
  };

  const handleConfirmJoin = async () => {
    const consultationId = consultation?.consultation_id;

    if (
      !consultationId ||
      joiningRef.current ||
      joinedRef.current ||
      !localStreamRef.current
    ) {
      return;
    }

    if (consultation.status !== "ONGOING") {
      return;
    }

    joiningRef.current = true;
    setJoining(true);
    setError("");

    try {
      console.log("[TeleConsultation] Joining consultation");

      await teleConsultationApi.joinConsultation(consultationId, {
        participant_type: "TECHNICIAN",
      });

      joinedRef.current = true;

      setPrejoin(false);
      setJoined(true);
      setRecordingMessage("");

      // Reduce REST polling to state monitoring only (single interval).
      startPolling(consultationId, POLL_JOINED_MS);

      startRtc(consultationId);
    } catch (requestError) {
      console.error("Join consultation error:", requestError);
      setError(getErrorMessage(requestError));
    } finally {
      joiningRef.current = false;
      setJoining(false);
    }
  };

  const performLeave = useCallback(async () => {
    if (leavingRef.current) {
      return;
    }

    leavingRef.current = true;
    setLeaving(true);

    const consultationId = consultationIdRef.current;

    try {
      if (isRecordingRef.current) {
        await stopRecording(true);
      }

      // Stops media, closes RTCPeerConnection and WebSocket, sends signaling "leave".
      teardownCall();

      if (consultationId && joinedRef.current !== undefined) {
        try {
          await teleConsultationApi.leaveConsultation(consultationId, {
            participant_type: "TECHNICIAN",
          });
        } catch (requestError) {
          console.error("Leave consultation error:", requestError);
        }
      }
    } finally {
      if (mountedRef.current) {
        setJoined(false);
        setPrejoin(false);
        setMicEnabled(true);
        setCameraEnabled(true);
        setLeaving(false);
      }

      leavingRef.current = false;

      if (consultationId && mountedRef.current) {
        startPolling(consultationId, POLL_WAITING_MS, true);
      }
    }
  }, [startPolling, stopRecording, teardownCall]);

  const handleToggleMic = () => {
    const stream = localStreamRef.current;
    const tracks = stream ? stream.getAudioTracks() : [];

    if (tracks.length === 0) {
      return;
    }

    const next = !tracks[0].enabled;

    tracks.forEach((track) => {
      track.enabled = next;
    });

    setMicEnabled(next);
  };

  const handleToggleCamera = () => {
    const stream = localStreamRef.current;
    const tracks = stream ? stream.getVideoTracks() : [];

    if (tracks.length === 0) {
      return;
    }

    const next = !tracks[0].enabled;

    tracks.forEach((track) => {
      track.enabled = next;
    });

    setCameraEnabled(next);
  };

  const handleBack = () => {
    if (joined || prejoin) {
      setShowLeaveConfirm(true);
      return;
    }

    navigate("/technician/mfi-dashboard");
  };

  const handleConfirmLeaveAndBack = async () => {
    setShowLeaveConfirm(false);

    if (joined) {
      await performLeave();
    } else {
      handleCancelPrejoin();
    }

    navigate("/technician/mfi-dashboard");
  };

  const handleNewConsultation = () => {
    if (creating) {
      return;
    }

    try {
      sessionStorage.removeItem(consultationStorageKey);
    } catch (storageError) {
      console.error(storageError);
    }

    cleanupPolling();
    consultationIdRef.current = null;
    consultationStatusRef.current = null;
    createStartedRef.current = false;

    setConsultation(null);
    setLoading(true);

    createConsultation(true);
  };

  const handleRetryCreate = () => {
    createStartedRef.current = false;
    setLoading(true);
    createConsultation();
  };

  /* ---------------- Effects ---------------- */

  useEffect(() => {
    mountedRef.current = true;

    createConsultation();

    return () => {
      mountedRef.current = false;
      cleanupPolling();
    };
  }, [createConsultation, cleanupPolling]);

  // Full cleanup on real unmount. Intentionally does NOT call /leave/.
  useEffect(() => {
    return () => {
      cleanupPolling();
      clearRecordingTimer();

      const recorder = mediaRecorderRef.current;

      if (recorder && recorder.state !== "inactive") {
        recorder.onstop = null;

        try {
          recorder.stop();
        } catch (stopError) {
          console.error(stopError);
        }
      }

      recordingCleanupRef.current?.();

      joinedRef.current = false;

      if (rtcClientRef.current) {
        rtcClientRef.current.close(true);
        rtcClientRef.current = null;
      }

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((track) => track.stop());
        localStreamRef.current = null;
      }
    };
  }, [cleanupPolling, clearRecordingTimer]);

  // Doctor ended / cancelled while technician is connected.
  useEffect(() => {
    const status = consultation?.status;

    if (!TERMINAL_STATUSES.includes(status)) {
      return;
    }

    cleanupPolling();

    if (!joinedRef.current && !joined && !prejoin) {
      return;
    }

    (async () => {
      if (isRecordingRef.current) {
        await stopRecording(true);
      }

      teardownCall();

      if (mountedRef.current) {
        setJoined(false);
        setPrejoin(false);
        setMicEnabled(true);
        setCameraEnabled(true);
      }
    })();
  }, [
    consultation?.status,
    joined,
    prejoin,
    cleanupPolling,
    stopRecording,
    teardownCall,
  ]);

  // Warn before closing/refreshing the tab during a call.
  useEffect(() => {
    if (!joined) {
      return undefined;
    }

    const handler = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handler);

    return () => window.removeEventListener("beforeunload", handler);
  }, [joined]);

  /* ---------------- Render ---------------- */

  const renderContent = () => {
    if (loading || creating) {
      return (
        <div style={styles.centerState}>
          <div className="tc-spinner" style={styles.spinner}></div>

          <h2 style={styles.stateTitle}>
            {creating ? "Creating consultation..." : "Loading consultation..."}
          </h2>

          <p style={styles.stateText}>
            Please wait while the consultation request is being prepared.
          </p>
        </div>
      );
    }

    if (error && !consultation) {
      return (
        <div style={styles.centerState}>
          <div style={styles.errorIcon}>!</div>

          <h2 style={styles.stateTitle}>Unable to create consultation</h2>

          <p style={styles.errorText}>{error}</p>

          <button
            type="button"
            onClick={handleRetryCreate}
            title="Try again"
            aria-label="Try again"
            style={styles.primaryButton}
          >
            Try Again
          </button>
        </div>
      );
    }

    if (!consultation) {
      return (
        <div style={styles.centerState}>
          <h2 style={styles.stateTitle}>Consultation unavailable</h2>

          <p style={styles.stateText}>
            No consultation information is available.
          </p>
        </div>
      );
    }

    if (TERMINAL_STATUSES.includes(consultation.status)) {
      return (
        <ConsultationSummary
          consultation={consultation}
          onBack={() => navigate("/technician/mfi-dashboard")}
          onNew={handleNewConsultation}
          creating={creating}
        />
      );
    }

    if (joined) {
      return (
        <CallScreen
          consultation={consultation}
          member={member}
          localVideoRef={localVideoRef}
          remoteVideoRef={remoteVideoRef}
          micEnabled={micEnabled}
          cameraEnabled={cameraEnabled}
          hasAudio={hasAudio}
          hasVideo={hasVideo}
          hasRemoteStream={hasRemoteStream}
          isWebSocketConnected={isWebSocketConnected}
          isWebRTCConnected={isWebRTCConnected}
          reconnecting={reconnecting}
          connectionError={connectionError}
          isRecording={isRecording}
          recordingDuration={recordingDuration}
          uploadingRecording={uploadingRecording}
          recordingMessage={recordingMessage}
          leaving={leaving}
          onToggleMic={handleToggleMic}
          onToggleCamera={handleToggleCamera}
          onToggleRecording={handleToggleRecording}
          onLeave={performLeave}
        />
      );
    }

    if (prejoin) {
      return (
        <PreJoin
          localVideoRef={localVideoRef}
          requestingMedia={requestingMedia}
          isMediaReady={isMediaReady}
          mediaError={mediaError}
          mediaWarning={mediaWarning}
          hasAudio={hasAudio}
          hasVideo={hasVideo}
          joining={joining}
          onRetry={handleRetryMedia}
          onConfirm={handleConfirmJoin}
          onCancel={handleCancelPrejoin}
          member={member}
        />
      );
    }

    return (
      <WaitingRoom
        consultation={consultation}
        doctorStarted={doctorStarted}
        busy={requestingMedia || joining}
        onJoin={handleOpenPrejoin}
      />
    );
  };

  const bannerMessage = error && consultation ? error : pollError;

  return (
    <div style={styles.page}>
      <style>{RESPONSIVE_CSS}</style>

      <header className="tc-header" style={styles.header}>
        <div>
          <div style={styles.headerTitle}>Teleconsultation</div>
          <div style={styles.headerSubtitle}>Rural Health Consultation</div>
        </div>

        <button
          type="button"
          onClick={handleBack}
          title="Back"
          aria-label="Back"
          style={styles.headerBackButton}
        >
          ← Back
        </button>
      </header>

      <main className="tc-main" style={styles.main}>
        <PatientInfoCard
          family={family}
          member={member}
          pkg={pkg}
          service={service}
        />

        {bannerMessage && consultation && (
          <div style={styles.warningBanner} role="alert">
            {bannerMessage}
          </div>
        )}

        {renderContent()}
      </main>

      {showLeaveConfirm && (
        <Modal
          title="Leave consultation?"
          actions={
            <>
              <button
                type="button"
                onClick={() => setShowLeaveConfirm(false)}
                title="Cancel"
                aria-label="Cancel"
                style={styles.secondaryButton}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleConfirmLeaveAndBack}
                title="Leave meeting"
                aria-label="Leave meeting"
                style={styles.dangerButton}
              >
                Leave Meeting
              </button>
            </>
          }
        >
          Are you sure you want to leave the consultation?
        </Modal>
      )}

      {showRecordNotice && (
        <Modal
          title="Start recording?"
          actions={
            <>
              <button
                type="button"
                onClick={() => setShowRecordNotice(false)}
                title="Cancel"
                aria-label="Cancel"
                style={styles.secondaryButton}
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={confirmStartRecording}
                title="Start recording"
                aria-label="Start recording"
                style={styles.primaryButton}
              >
                Start Recording
              </button>
            </>
          }
        >
          Recording will capture the consultation audio/video.
        </Modal>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const RESPONSIVE_CSS = `
@keyframes tcSpin { to { transform: rotate(360deg); } }
@keyframes tcPulse { 0%, 100% { opacity: .35; transform: scale(.8); } 50% { opacity: 1; transform: scale(1); } }
@keyframes tcRec { 50% { opacity: .35; } }

.tc-spinner { animation: tcSpin 0.9s linear infinite; }
.tc-dot { animation: tcPulse 1.2s ease-in-out infinite; }
.tc-rec { animation: tcRec 1.2s ease-in-out infinite; }

.tc-info-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
.tc-meta-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin: 22px 0; text-align: left; }
.tc-summary-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; margin: 24px 0; text-align: left; }

.tc-video-area { height: min(68vh, 640px); min-height: 380px; }
.tc-local { width: 220px; height: 150px; }

@media (max-width: 1024px) {
  .tc-info-grid, .tc-meta-grid, .tc-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .tc-main { padding: 22px 20px !important; }
  .tc-header { padding: 16px 20px !important; }
}

@media (max-width: 640px) {
  .tc-info-grid, .tc-meta-grid, .tc-summary-grid { grid-template-columns: minmax(0, 1fr); }
  .tc-main { padding: 14px 12px !important; }
  .tc-header { padding: 12px 14px !important; }
  .tc-video-area { height: 62vh; min-height: 340px; }
  .tc-local { width: 108px !important; height: 146px !important; }
  .tc-controls { gap: 10px !important; padding: 12px 10px !important; flex-wrap: wrap; }
}

button:focus-visible { outline: 3px solid rgba(17, 168, 164, 0.45); outline-offset: 2px; }

@media (prefers-reduced-motion: reduce) {
  .tc-spinner, .tc-dot, .tc-rec { animation: none; }
}
`;

const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#f8fafc",
    fontFamily:
      "Inter, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: COLORS.darkText,
  },

  header: {
    backgroundColor: COLORS.white,
    borderBottom: `1px solid ${COLORS.mediumGrey}`,
    padding: "18px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
  },

  headerTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: COLORS.darkText,
  },

  headerSubtitle: {
    marginTop: "4px",
    fontSize: "13px",
    color: COLORS.mediumText,
  },

  headerBackButton: {
    border: `1px solid ${COLORS.mediumGrey}`,
    backgroundColor: COLORS.white,
    color: COLORS.darkText,
    borderRadius: "8px",
    padding: "9px 15px",
    cursor: "pointer",
    fontWeight: 600,
    fontSize: "14px",
  },

  main: {
    maxWidth: "1400px",
    margin: "0 auto",
    padding: "28px 32px",
    boxSizing: "border-box",
    width: "100%",
  },

  card: {
    backgroundColor: COLORS.white,
    borderRadius: "14px",
    border: `1px solid ${COLORS.mediumGrey}`,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    padding: "22px",
    marginBottom: "22px",
  },

  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "18px",
    gap: "12px",
  },

  cardTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 700,
    color: COLORS.darkText,
  },

  cardSubtitle: {
    margin: "4px 0 0",
    fontSize: "13px",
    color: COLORS.mediumText,
  },

  patientAvatar: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    backgroundColor: COLORS.aquaBlue,
    color: COLORS.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "18px",
    flexShrink: 0,
  },

  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "12px 14px",
    backgroundColor: COLORS.lightGrey,
    borderRadius: "10px",
    minWidth: 0,
  },

  infoLabel: {
    fontSize: "12px",
    color: COLORS.mediumText,
    fontWeight: 500,
  },

  infoValue: {
    fontSize: "14px",
    fontWeight: 600,
    color: COLORS.darkText,
    wordBreak: "break-word",
  },

  statusBadge: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: 700,
    letterSpacing: "0.3px",
  },

  warningBanner: {
    backgroundColor: "#fffbeb",
    border: "1px solid #fcd34d",
    color: "#92400e",
    borderRadius: "10px",
    padding: "12px 16px",
    marginBottom: "18px",
    fontSize: "14px",
  },

  /* waiting room */
  waitingCard: {
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.mediumGrey}`,
    borderRadius: "14px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    padding: "36px 28px",
    maxWidth: "720px",
    margin: "0 auto",
    textAlign: "center",
    boxSizing: "border-box",
  },

  waitingIcon: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    backgroundColor: "#e6f6f5",
    color: COLORS.aquaBlue,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "34px",
    margin: "0 auto 18px",
  },

  waitingIconSuccess: {
    backgroundColor: "#dcfce7",
    color: "#15803d",
  },

  waitingTitle: {
    margin: "0 0 8px",
    fontSize: "22px",
    fontWeight: 700,
  },

  waitingDescription: {
    margin: "0 auto",
    maxWidth: "520px",
    color: COLORS.mediumText,
    fontSize: "14px",
    lineHeight: 1.6,
  },

  metaItem: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    padding: "12px 14px",
    backgroundColor: COLORS.lightGrey,
    borderRadius: "10px",
    minWidth: 0,
  },

  metaLabel: {
    fontSize: "12px",
    color: COLORS.mediumText,
  },

  metaValue: {
    fontSize: "13px",
    fontWeight: 600,
    wordBreak: "break-all",
  },

  waitingAnimation: {
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    color: COLORS.mediumText,
    fontSize: "14px",
    marginTop: "4px",
  },

  dot: {
    width: "12px",
    height: "12px",
    borderRadius: "50%",
    backgroundColor: COLORS.aquaBlue,
    display: "inline-block",
  },

  /* pre-join */
  prejoinCard: {
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.mediumGrey}`,
    borderRadius: "14px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    padding: "28px",
    maxWidth: "720px",
    margin: "0 auto",
    textAlign: "center",
    boxSizing: "border-box",
  },

  prejoinTitle: {
    margin: "0 0 6px",
    fontSize: "22px",
    fontWeight: 700,
  },

  prejoinText: {
    margin: "0 0 18px",
    color: COLORS.mediumText,
    fontSize: "14px",
  },

  prejoinPreview: {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    backgroundColor: COLORS.darkGrey,
    borderRadius: "12px",
    overflow: "hidden",
    marginBottom: "16px",
  },

  prejoinPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    color: "#d1d5db",
    fontSize: "14px",
  },

  deviceRow: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "14px",
  },

  deviceChip: {
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "13px",
    fontWeight: 600,
  },

  deviceChipOk: {
    backgroundColor: "#dcfce7",
    color: "#15803d",
  },

  deviceChipOff: {
    backgroundColor: COLORS.lightGrey,
    color: COLORS.mediumText,
  },

  inlineWarning: {
    backgroundColor: "#fffbeb",
    border: "1px solid #fcd34d",
    color: "#92400e",
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "13px",
    marginBottom: "14px",
    textAlign: "left",
  },

  inlineError: {
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca",
    color: COLORS.red,
    borderRadius: "10px",
    padding: "10px 14px",
    fontSize: "13px",
    marginBottom: "14px",
    textAlign: "left",
  },

  buttonRow: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: "12px",
    marginTop: "8px",
  },

  /* call screen */
  videoContainer: {
    backgroundColor: COLORS.darkGrey,
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 4px 16px rgba(15, 23, 42, 0.18)",
  },

  videoHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    padding: "12px 18px",
    backgroundColor: "#2f2e32",
    color: COLORS.white,
  },

  videoHeaderLeft: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "14px",
  },

  videoHeaderTitle: {
    fontSize: "15px",
  },

  connectedStatus: {
    fontSize: "13px",
    fontWeight: 600,
  },

  recBadge: {
    fontSize: "12px",
    fontWeight: 700,
    color: COLORS.white,
    backgroundColor: COLORS.red,
    padding: "3px 10px",
    borderRadius: "999px",
  },

  videoRoom: {
    fontSize: "12px",
    color: COLORS.lightText,
    wordBreak: "break-all",
  },

  videoArea: {
    position: "relative",
    width: "100%",
    backgroundColor: "#111827",
    overflow: "hidden",
  },

  remoteVideo: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    backgroundColor: "#111827",
  },

  doctorPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: COLORS.white,
    padding: "16px",
  },

  doctorAvatar: {
    width: "96px",
    height: "96px",
    borderRadius: "50%",
    backgroundColor: COLORS.aquaBlue,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "40px",
    fontWeight: 700,
    marginBottom: "14px",
  },

  doctorPlaceholderTitle: {
    margin: "0 0 6px",
    fontSize: "20px",
  },

  doctorPlaceholderText: {
    margin: 0,
    color: COLORS.lightText,
    fontSize: "14px",
  },

  remoteLabel: {
    position: "absolute",
    left: "14px",
    bottom: "14px",
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    color: COLORS.white,
    padding: "4px 10px",
    borderRadius: "6px",
    fontSize: "13px",
  },

  localPreview: {
    position: "absolute",
    right: "14px",
    bottom: "14px",
    borderRadius: "12px",
    overflow: "hidden",
    backgroundColor: "#1f2937",
    border: "2px solid rgba(255, 255, 255, 0.85)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.35)",
  },

  mirroredVideo: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scaleX(-1)",
    backgroundColor: "#1f2937",
  },

  cameraOff: {
    position: "absolute",
    inset: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    color: "#d1d5db",
    fontSize: "12px",
    backgroundColor: "#1f2937",
  },

  localLabel: {
    position: "absolute",
    left: "6px",
    bottom: "6px",
    backgroundColor: "rgba(0, 0, 0, 0.55)",
    color: COLORS.white,
    padding: "2px 8px",
    borderRadius: "6px",
    fontSize: "11px",
  },

  smallAvatar: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    backgroundColor: COLORS.aquaBlue,
    color: COLORS.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "18px",
  },

  largeAvatar: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    backgroundColor: COLORS.aquaBlue,
    color: COLORS.white,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: "30px",
  },

  callNotice: {
    backgroundColor: "#3b3a3f",
    color: "#fcd34d",
    fontSize: "13px",
    textAlign: "center",
    padding: "8px 14px",
  },

  callControls: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    backgroundColor: "#2f2e32",
  },

  controlButton: {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    border: "none",
    backgroundColor: "#4b4a50",
    color: COLORS.white,
    fontSize: "20px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  controlButtonDanger: {
    backgroundColor: COLORS.red,
  },

  controlButtonRecording: {
    backgroundColor: COLORS.red,
  },

  leaveButton: {
    height: "52px",
    padding: "0 24px",
    borderRadius: "999px",
    border: "none",
    backgroundColor: COLORS.red,
    color: COLORS.white,
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
  },

  /* summary */
  summaryCard: {
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.mediumGrey}`,
    borderRadius: "14px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.06)",
    padding: "36px 28px",
    maxWidth: "820px",
    margin: "0 auto",
    textAlign: "center",
    boxSizing: "border-box",
  },

  completedIcon: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    backgroundColor: "#dcfce7",
    color: "#15803d",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "34px",
    margin: "0 auto 16px",
  },

  cancelledIcon: {
    backgroundColor: "#fee2e2",
    color: COLORS.red,
  },

  summaryTitle: {
    margin: "0 0 8px",
    fontSize: "22px",
    fontWeight: 700,
  },

  summaryDescription: {
    margin: 0,
    color: COLORS.mediumText,
    fontSize: "14px",
  },

  /* generic states */
  centerState: {
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.mediumGrey}`,
    borderRadius: "14px",
    padding: "48px 24px",
    maxWidth: "720px",
    margin: "0 auto",
    textAlign: "center",
    boxSizing: "border-box",
  },

  spinner: {
    width: "42px",
    height: "42px",
    border: `4px solid ${COLORS.mediumGrey}`,
    borderTopColor: COLORS.aquaBlue,
    borderRadius: "50%",
    margin: "0 auto 18px",
  },

  stateTitle: {
    margin: "0 0 8px",
    fontSize: "20px",
    fontWeight: 700,
  },

  stateText: {
    margin: 0,
    color: COLORS.mediumText,
    fontSize: "14px",
  },

  errorIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    backgroundColor: "#fee2e2",
    color: COLORS.red,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "28px",
    fontWeight: 700,
    margin: "0 auto 16px",
  },

  errorText: {
    margin: "0 0 20px",
    color: COLORS.red,
    fontSize: "14px",
  },

  /* buttons */
  primaryButton: {
    backgroundColor: COLORS.aquaBlue,
    color: COLORS.white,
    border: "none",
    borderRadius: "10px",
    padding: "12px 26px",
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
    marginTop: "8px",
  },

  secondaryButton: {
    backgroundColor: COLORS.white,
    color: COLORS.darkText,
    border: `1px solid ${COLORS.mediumGrey}`,
    borderRadius: "10px",
    padding: "12px 26px",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
    marginTop: "8px",
  },

  dangerButton: {
    backgroundColor: COLORS.red,
    color: COLORS.white,
    border: "none",
    borderRadius: "10px",
    padding: "12px 26px",
    fontSize: "15px",
    fontWeight: 700,
    cursor: "pointer",
    marginTop: "8px",
  },

  /* modal */
  modalOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(17, 24, 39, 0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    zIndex: 1000,
  },

  modal: {
    backgroundColor: COLORS.white,
    borderRadius: "14px",
    padding: "24px",
    width: "100%",
    maxWidth: "420px",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.25)",
    boxSizing: "border-box",
  },

  modalTitle: {
    margin: "0 0 10px",
    fontSize: "18px",
    fontWeight: 700,
  },

  modalBody: {
    color: COLORS.mediumText,
    fontSize: "14px",
    lineHeight: 1.6,
  },

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: "10px",
    marginTop: "16px",
  },
};

export default TechnicianTeleServices;